import moment from 'moment';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  CheckBoxField,
  DateRange,
  Drawers,
  FlexColumn,
  FlexEvenly,
  FlexSpace,
  FormModal,
  Header,
  Header3,
  Label,
  Loader,
  SmallBody,
  TextWithTooltip
} from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import { SORT_BY_DATE } from '@ubeya/shared/utils/sorting';
import useCRM from '@ubeya/shared/hooks/account/useCRM';
import useSupplier from '@ubeya/shared/hooks/account/useSupplier';
import useToaster from '@ubeya/shared/hooks/useToaster';
import usePersistedState from '@ubeya/shared-web/hooks/usePersistedState';
import * as api from '@ubeya/shared/services/api';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import { LABOR_DESK_ACCOUNT_ID } from '@ubeya/shared/utils/accounts';
import { EmptyState as DefaultEmptyState } from 'shared/components';
import { generateSummarizedClientInvoiceReportCSV, generateXEROClientInvoiceReportCSV } from 'shared/utils/payroll';
import Section from './InvoiceSection';
import {
  HARD_CODED_ISRAEL_VAT,
  HARD_CODED_ISRAEL_VAT_PRECENTAGE,
  HARD_CODED_LABOR_DESK_VAT,
  HARD_CODED_LABOR_DESK_VAT_PRECENTAGE
} from 'payroll/constants';

const Container = styled(FlexColumn)`
  flex: 1;
  margin-left: 10px;
`;

const HeaderRightSideWrapper = styled(FlexSpace)`
  flex: 1;
  gap: 8px;

  .label {
    color: ${({ theme }) => theme.colors.white};
  }
`;

const Link = styled.a`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: underline;
  margin-left: 5px;
`;

const GridHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $includeVAT }) => ($includeVAT ? 9 : 7)}, 1fr);
  margin: 0 20px;
`;

const Grid = styled(GridHeader)`
  margin: 5px 20px;
`;

const GridTitle = styled(Header3)`
  font-weight: 500;
`;

const EmptyState = ({ t }) => (
  <DefaultEmptyState text={t('noInvoicesForThisPeriod')}>
    <SmallBody>{t('noPayrollDataDesc')} </SmallBody>
  </DefaultEmptyState>
);

const SendCopy = styled.div`
  flex: 1;
`;

const ModalContainer = styled.div`
  margin-top: 20px;
  margin-bottom: 15px;
  margin-left: 10px;
`;

const OptionContainer = styled(FlexEvenly)`
  align-items: center;
  justify-content: flex-start;
  margin: 8px 0;
`;

const OptionName = styled(TextWithTooltip)`
  width: 150px;
`;

const SupplierAdminsModal = ({ supplierId, onClose, onConfirm }) => {
  const { t } = useContext(ConfigContext);
  const { data: supplierData } = useSupplier({ supplierId });

  const supplierAdmins = useMemo(() => {
    const addedSupplierIds = new Set();
    return (supplierData?.branches || []).reduce((output, { admins = [] }) => {
      admins.forEach(({ id, firstName, lastName, email }) => {
        if (!addedSupplierIds.has(id)) {
          output.push({ id, firstName, lastName, email });
          addedSupplierIds.add(id);
        }
      });

      return output;
    }, []);
  }, [supplierData?.branches]);

  const initalValues = useMemo(
    () => ({
      admins: {}
    }),
    []
  );

  const handleSubmit = useCallback(
    (values) => {
      const supplierAdminIds = Object.entries(values?.admins || [])
        .filter(([_, val]) => !!val)
        .map(([key]) => key.split('-')?.[1])
        .filter((val) => !!val)
        .map((val) => Number(val));

      onConfirm({ supplierAdminIds, sendCopy: values?.sendCopy });
      onClose();
    },
    [onClose, onConfirm]
  );

  return (
    <FormModal
      initialValues={initalValues}
      disabledOnPristine={false}
      onSubmit={handleSubmit}
      onClose={onClose}
      confirmText={t('continue')}
      isPaddingContent={false}
      size="small"
      disabledConfirm={!supplierAdmins.length}
      title="Select suppliers to send email to"
      renderButtons={() => (
        <SendCopy>
          <CheckBoxField key={0} name="sendCopy" title="Send myself a copy" />
        </SendCopy>
      )}>
      <ModalContainer>
        {supplierAdmins.map(({ id, firstName, lastName, email }) => (
          <OptionContainer key={id}>
            <CheckBoxField
              name={`admins.admin-${id}`}
              title={<OptionName>{[firstName, lastName].filter((val) => !!val).join(' ')}</OptionName>}
            />
            <Label>{email}</Label>
          </OptionContainer>
        ))}
        {!supplierAdmins.length && <p>{t('clientEmailsModalEmptyAdmins')}</p>}
      </ModalContainer>
    </FormModal>
  );
};

const ColumnsModal = ({ includeVAT, isFlatWage, isPayRatesEnabled, onClose, onConfirm }) => {
  const { t } = useContext(ConfigContext);
  const { addResponseError } = useToaster();
  const [supplierInvoiceColumns, setSupplierInvoiceColumns] = usePersistedState('supplier-invoice-columns', {});

  const initalValues = useMemo(
    () => ({
      columns: supplierInvoiceColumns
    }),
    [supplierInvoiceColumns]
  );

  const columnsOptions = useMemo(
    () => [
      { slug: 'approved', dataKey: 'approved' },
      { slug: 'poNumber', dataKey: 'poNumber' },
      { slug: 'eventName', dataKey: 'projectName' },
      { slug: 'shiftArea', dataKey: 'location' },
      { slug: 'clientName', dataKey: 'clientName' },
      { slug: 'checkInTime', dataKey: 'checkIn' },
      { slug: 'checkOutTime', dataKey: 'checkOut' },
      { slug: 'breaks', dataKey: 'breaktimes' },
      { slug: 'total', dataKey: 'total' },
      { slug: 'clientStatus', dataKey: 'clientStatus' },
      { slug: 'costCenter', dataKey: 'costCenter' },
      { slug: 'clientCostCenter', dataKey: 'clientCostCenter' },
      { slug: 'reviewOnEmployee', dataKey: 'review' },
      { slug: 'invoiceNumber', dataKey: 'invoiceNumber' },
      { slug: 'clientInvoiceNumber', dataKey: 'clientInvoiceNumber' },
      { slug: 'regular', dataKey: 'regular' },
      { slug: 'overtime', dataKey: 'overtime' },
      { slug: 'overtime2', dataKey: 'overtime2' },
      { slug: 'holidayRegular', dataKey: 'holidayRegular' },
      { slug: 'holidayOvertime', dataKey: 'holidayOvertime' },
      { slug: 'holidayOvertime2', dataKey: 'holidayOvertime2' },
      // The following columns, except billRate are Unsupported in BE - these columns will always be empty.
      ...(includeVAT ? [{ slug: 'totalWithVAT', dataKey: 'totalWithVAT' }] : []),
      ...(isFlatWage
        ? [
            { slug: 'globalWage', dataKey: 'globalWage' },
            { slug: 'flatHours', dataKey: 'flatHours' }
          ]
        : []),
      ...(!isPayRatesEnabled
        ? [{ slug: 'clientWage', dataKey: 'billRate' }]
        : [{ slug: 'hourlyRate', dataKey: 'hourlyRate' }])
    ],
    [includeVAT, isFlatWage, isPayRatesEnabled]
  );

  const handleSubmit = useCallback(
    async (values) => {
      const columns = Object.entries(values?.columns || [])
        .filter(([_, val]) => !!val)
        .map(([key]) => key.split('-')?.[1])
        .filter((val) => !!val)
        .map((val) => String(val));

      if (!columns.length) {
        addResponseError(new Error('No column selected'));
        return;
      }

      setSupplierInvoiceColumns(values?.columns);

      await onConfirm({ columns });
      onClose();
    },
    [onClose, onConfirm, addResponseError, setSupplierInvoiceColumns]
  );

  return (
    <FormModal
      initialValues={initalValues}
      disabledOnPristine={false}
      onSubmit={handleSubmit}
      onClose={onClose}
      confirmText={t('continue')}
      isPaddingContent={false}
      size="small"
      disabledConfirm={!columnsOptions.length}
      title="Select columns">
      <ModalContainer>
        {columnsOptions.map(({ slug, dataKey }) => (
          <OptionContainer key={slug}>
            <CheckBoxField name={`columns.column-${dataKey}`} title={<OptionName>{t(slug)}</OptionName>} />
          </OptionContainer>
        ))}
      </ModalContainer>
    </FormModal>
  );
};

const Invoices = ({
  timeRange,
  setTimeRange,
  isLoading,
  clientId,
  InvoicesModalComponent,
  invoicesModalParams,
  payroll = [],
  payrollTotal = 0,
  useClientProject = true,
  isSupplierInvoices = false
}) => {
  const { t, countryCode, shortDateFormat, isPayRatesEnabled } = useContext(ConfigContext);
  const { accountId, account } = useAccount();
  const { addResponseError } = useToaster();
  const formattingMap = useFormattingMap();
  const { mappedClients } = useCRM();

  const [dataForEmailRequest, setDataForEmailRequest] = useState({});
  const [showSupplierAdminsModal, setShowSupplierAdminsModal] = useState(false);
  const [showColumnsModal, setShowColumnsModal] = useState(false);

  const dateSorter = SORT_BY_DATE('projectDate');

  const includeVAT = countryCode?.toLowerCase() === 'il' || accountId === LABOR_DESK_ACCOUNT_ID;

  const selectedVatValuePercentage = includeVAT
    ? accountId === LABOR_DESK_ACCOUNT_ID
      ? HARD_CODED_LABOR_DESK_VAT_PRECENTAGE
      : HARD_CODED_ISRAEL_VAT_PRECENTAGE
    : null;

  const selectedVatValue = includeVAT
    ? accountId === LABOR_DESK_ACCOUNT_ID
      ? HARD_CODED_LABOR_DESK_VAT
      : HARD_CODED_ISRAEL_VAT
    : null;

  const isFlatWage = account.payrollConfig.flatIsActive && !isPayRatesEnabled;

  const exportSummarizedReportCSV = useCallback(
    (e) => {
      e.stopPropagation();

      //TODO: IMPLEMENT FOR SUPPLIER
      generateSummarizedClientInvoiceReportCSV({
        accountId,
        projects: payroll,
        t,
        includeVAT,
        timeRange,
        shortDateFormat,
        filePrefix: invoicesModalParams?.filePrefix || account?.name
      });
    },
    [invoicesModalParams, accountId, payroll, t, includeVAT, timeRange, shortDateFormat, account?.name]
  );

  const exportXEROReportCSV = useCallback(
    (e) => {
      e.stopPropagation();

      const sectionStart = moment(timeRange.start).format(API_DATE_FORMAT);
      const sectionEnd = moment(timeRange.end).format(API_DATE_FORMAT);

      const fileName = t(moment(`${sectionStart}-${sectionEnd}`, 'YYYY-MM').format('MMM YYYY'));

      generateXEROClientInvoiceReportCSV({
        projects: payroll.map((project) => ({ ...project, clientId })),
        t,
        mappedClients,
        shortDateFormat,
        fileName
      });
    },
    [timeRange.start, timeRange.end, t, payroll, mappedClients, shortDateFormat, clientId]
  );

  const noData = payroll.length === 0;

  const onClickSendToAgency = useCallback((event) => {
    event.preventDefault();
    setShowSupplierAdminsModal(true);
  }, []);

  const onCloseSupplierAdminsModal = useCallback(() => setShowSupplierAdminsModal(false), []);

  const onCloseColumnsModal = useCallback(() => setShowColumnsModal(false), []);

  const onConfirmColumnsModal = useCallback(
    async ({ columns }) => {
      try {
        await api.sendSupplierInvoiceEmail({
          ...dataForEmailRequest,
          columns,
          from: moment(timeRange.start).format(API_DATE_FORMAT),
          to: moment(timeRange.end).format(API_DATE_FORMAT),
          supplierId: invoicesModalParams.supplierId,
          accountId
        });
      } catch (e) {
        addResponseError(e);
      }
    },
    [timeRange.end, timeRange.start, invoicesModalParams, accountId, dataForEmailRequest, addResponseError]
  );

  const onConfirmSupplierAdminsModal = useCallback(({ supplierAdminIds, sendCopy }) => {
    setDataForEmailRequest((prev) => ({
      ...prev,
      supplierAdminIds,
      sendCopy
    }));
    setShowColumnsModal(true);
  }, []);

  const HeaderRightSide = useMemo(() => {
    const DateRangeComponent = (
      <DateRange value={timeRange} onChange={setTimeRange} hideToday valueAsString openFromRight />
    );
    if (isSupplierInvoices) {
      return (
        <HeaderRightSideWrapper>
          <Button onClick={onClickSendToAgency} disabled={noData} primary narrow>
            {t('sendToAgency')}
          </Button>
          {DateRangeComponent}
        </HeaderRightSideWrapper>
      );
    }
    return DateRangeComponent;
  }, [timeRange, setTimeRange, isSupplierInvoices, onClickSendToAgency, noData, t]);

  return (
    <Container>
      <Header title={`${t('finance')}`} rightSide={HeaderRightSide} combined />
      {isLoading ? (
        <Loader />
      ) : noData ? (
        <EmptyState t={t} />
      ) : (
        <Drawers>
          <Drawers.Section
            defaultOpen
            title={
              <Section
                invoicesModalParams={invoicesModalParams}
                total={payrollTotal}
                formattingMap={formattingMap}
                t={t}
                InvoicesModalComponent={InvoicesModalComponent}
                countryCode={countryCode}
                clientId={clientId}
                timeRange={timeRange}
                handleExportSummarizedReport={(e) => exportSummarizedReportCSV(e)}
                handleExportXEROReport={(e) => exportXEROReportCSV(e)}
              />
            }>
            <GridHeader $includeVAT={includeVAT}>
              <GridTitle>{t('date')}</GridTitle>
              <GridTitle>{t('name')}</GridTitle>
              <GridTitle># {t('employees')}</GridTitle>
              <GridTitle>{t('basicBill')}</GridTitle>
              <GridTitle>{t('additionalCharges')}</GridTitle>
              <GridTitle>{t('total')}</GridTitle>
              {includeVAT && (
                <>
                  <GridTitle>{t('vat')}</GridTitle>
                  <GridTitle>{t('totalWithVAT')}</GridTitle>
                </>
              )}
            </GridHeader>

            {payroll
              .sort(dateSorter)
              .map(
                ({
                  projectDate,
                  projectName,
                  projectId,
                  clientBranchId,
                  branchId,
                  clientProjectId,
                  employees,
                  additionalRevenues,
                  total: totalAmount,
                  totalWithAdditional = 0
                }) => (
                  <Grid key={projectId} $includeVAT={includeVAT}>
                    <div>{moment(projectDate).format(shortDateFormat)}</div>
                    <Link
                      href={`/${accountId}/scheduling/branch/${useClientProject ? clientBranchId : branchId}/project/${
                        useClientProject ? clientProjectId : projectId
                      }`}
                      target="_blank">
                      {projectName}
                    </Link>
                    <div>{employees || '0'}</div>
                    <div>{totalAmount ? `${formattingMap.currency(totalAmount)}` : '-'}</div>
                    <div>{additionalRevenues ? `${formattingMap.currency(additionalRevenues)}` : '-'}</div>
                    <div>{totalWithAdditional ? `${formattingMap.currency(totalWithAdditional)}` : '-'}</div>
                    {includeVAT && (
                      <>
                        <div>{selectedVatValuePercentage}%</div>

                        <div>
                          {totalWithAdditional
                            ? `${formattingMap.currency(totalWithAdditional * selectedVatValue)}`
                            : '-'}
                        </div>
                      </>
                    )}
                  </Grid>
                )
              )}
          </Drawers.Section>
        </Drawers>
      )}

      {showSupplierAdminsModal && (
        <SupplierAdminsModal
          supplierId={invoicesModalParams.supplierId}
          onConfirm={onConfirmSupplierAdminsModal}
          onClose={onCloseSupplierAdminsModal}
        />
      )}

      {showColumnsModal && (
        <ColumnsModal
          includeVAT={includeVAT}
          isFlatWage={isFlatWage}
          isPayRatesEnabled={isPayRatesEnabled}
          onConfirm={onConfirmColumnsModal}
          onClose={onCloseColumnsModal}
        />
      )}
    </Container>
  );
};

export default React.memo(Invoices);
